import { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { LoginPage } from '@features/auth/components/LoginPage';
import { UnauthorizedPage } from '@features/auth/components/UnauthorizedPage';
import { DashboardLayout } from '@app/layouts';
import { ProtectedRoute } from './ProtectedRoute';
import { UserRole } from '@features/auth/types';
import { PageSkeleton } from '@shared/components/LoadingSkeletons';

// Helper to wrap lazy components with Suspense
const Load = (Component: React.ComponentType<any>) => (
  <Suspense fallback={<PageSkeleton />}>
    <Component />
  </Suspense>
);

// ==================== LAZY IMPORTS ====================

// Admin Pages
const DashboardHome = lazy(() => import('@features/admin/pages/DashboardHome').then(m => ({ default: m.DashboardHome })));
const AdminPage = lazy(() => import('@features/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const CabAdminPage = lazy(() => import('@features/admin/pages/CabAdminPage').then(m => ({ default: m.CabAdminPage })));
const JcbAdminPage = lazy(() => import('@features/admin/pages/JcbAdminPage').then(m => ({ default: m.JcbAdminPage })));
const HandymanAdminPage = lazy(() => import('@features/admin/pages/HandymanAdminPage').then(m => ({ default: m.HandymanAdminPage })));
const EnquiriesPage = lazy(() => import('@features/admin/pages/EnquiriesPage').then(m => ({ default: m.EnquiriesPage })));
const ReportsPage = lazy(() => import('@features/admin/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AdminAccessManagement = lazy(() => import('@features/admin/pages/AdminAccessManagement').then(m => ({ default: m.AdminAccessManagement })));
const AdminUsersPage = lazy(() => import('@features/admin/pages/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const ApiTestPage = lazy(() => import('@features/admin/pages/ApiTestPage').then(m => ({ default: m.ApiTestPage })));

// Admin Settings (Default Exports)
const CitiesList = lazy(() => import('@features/admin/pages/CitiesList'));
const BranchesList = lazy(() => import('@features/admin/pages/BranchesList'));
const GoodsTypesList = lazy(() => import('@features/admin/pages/GoodsTypesList'));
const BannersList = lazy(() => import('@features/admin/pages/BannersList'));
const CancelReasonsList = lazy(() => import('@features/admin/pages/CancelReasonsList'));
const ProvidersPage = lazy(() => import('@features/admin/pages/ProvidersPage'));
const ServiceGalleriesManagement = lazy(() => import('@features/admin/pages/ServiceGalleriesManagement'));
const FAQsList = lazy(() => import('@features/admin/pages/FAQsList'));

// Booking Pages
const BookingsPage = lazy(() => import('@features/booking/pages/BookingsPage').then(m => ({ default: m.BookingsPage })));
const LiveOrderTrackingPage = lazy(() => import('@features/booking/pages/LiveOrderTrackingPage'));

// Service Management Pages
const ServicesHub = lazy(() => import('@/features/services/pages/ServicesHub'));
const ServicesPage = lazy(() => import('@/features/services/pages/ServicesPage'));
const ServiceSettingsPage = lazy(() => import('@/features/admin/pages/ServiceSettingsPage'));
const ServiceDetailPage = lazy(() => import('@features/services/pages/ServiceDetailPage'));
const ServiceGalleryPage = lazy(() => import('@features/services/pages/ServiceGalleryPage'));
const ServiceFAQsPage = lazy(() => import('@features/services/pages/ServiceFAQsPage'));
const ServiceSubServicesPage = lazy(() => import('@features/services/pages/ServiceSubServicesPage'));
const ServiceVehiclesPage = lazy(() => import('@features/services/pages/ServiceVehiclesPage'));

// Location Pages
const CitiesPage = lazy(() => import('@features/location/pages/CitiesPage'));

// Role Pages
const RolesPage = lazy(() => import('@features/role/pages/RolesPage'));

// Customer Pages
const CustomersPage = lazy(() => import('@features/customer/pages/CustomersPage').then(m => ({ default: m.CustomersPage })));

// Driver Pages
const DriversPage = lazy(() => import('@features/driver/pages/DriversPage').then(m => ({ default: m.DriversPage })));
const DriverKycDetailPage = lazy(() => import('@features/driver/pages/DriverKycDetailPage').then(m => ({ default: m.DriverKycDetailPage })));

// Goods Pages
const GoodsDashboard = lazy(() => import('@features/goods').then(m => ({ default: m.GoodsDashboard })));
const GoodsDriversList = lazy(() => import('@features/goods').then(m => ({ default: m.GoodsDriversList })));
const DriverProfile = lazy(() => import('@features/goods').then(m => ({ default: m.DriverProfile })));
const DriverWallet = lazy(() => import('@features/goods').then(m => ({ default: m.DriverWallet })));
const DriverRides = lazy(() => import('@features/goods').then(m => ({ default: m.DriverRides })));
const DriverRatings = lazy(() => import('@features/goods').then(m => ({ default: m.DriverRatings })));

// Fleet Pages (Assuming named exports from index or specific files, using barrel for simplicity with named destructuring)
const FleetPage = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.FleetPage })));
const VehiclesManagement = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehiclesManagement })));
const VehicleTypesManagement = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehicleTypesManagement })));
const VehicleBasePricesManagement = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehicleBasePricesManagement })));
const VehiclePriceTypesManagement = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehiclePriceTypesManagement })));
const VehiclePricePage = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehiclePricePage })));
const VehiclePeakHoursPage = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehiclePeakHoursPage })));
const VehicleUpgradePricesPage = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehicleUpgradePricesPage })));
const VehicleGuidelinesPage = lazy(() => import('@features/fleet/pages').then(m => ({ default: m.VehicleGuidelinesPage })));
const PricingRulesPage = lazy(() => import('@features/fleet/pages/PricingRulesPage'));

// New Feature Pages
const CouponsPage = lazy(() => import('@features/admin/pages/CouponsPage').then(m => ({ default: m.CouponsPage })));
const ControlSettingsPage = lazy(() => import('@features/admin/pages/ControlSettingsPage').then(m => ({ default: m.ControlSettingsPage })));
const RechargePlansPage = lazy(() => import('@features/admin/pages/RechargePlansPage').then(m => ({ default: m.RechargePlansPage })));
const ReferralAdminPage = lazy(() => import('@features/admin/pages/ReferralAdminPage').then(m => ({ default: m.ReferralAdminPage })));
const AuditLogsPage = lazy(() => import('@features/admin/pages/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // ==================== DASHBOARD ====================
      {
        path: 'dashboard',
        element: Load(DashboardHome),
      },
      // Dashboard Admin Panels
      {
        path: 'goods-admin',
        element: <Navigate to="/goods-details/dashboard" replace />,
      },
      {
        path: 'cab-admin',
        element: Load(CabAdminPage),
      },
      {
        path: 'jcb-admin',
        element: Load(JcbAdminPage),
      },
      {
        path: 'drivers-admin',
        element: Load(DriversPage),
      },
      {
        path: 'handyman-admin',
        element: Load(HandymanAdminPage),
      },

      // ==================== WEBSITE REQUESTS ====================
      {
        path: 'enquiries',
        element: Load(EnquiriesPage),
      },
      {
        path: 'enquiries/all',
        element: Load(EnquiriesPage),
      },
      {
        path: 'enquiries/agents',
        element: Load(EnquiriesPage),
      },

      // ==================== SERVICES MANAGEMENT ====================
      {
        path: 'services',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['CATEGORY_READ']}>
            {Load(ServicesPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'services-old',
        element: Load(ServicesHub),
      },
      {
        path: 'services/:id',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['CATEGORY_READ']}>
            {Load(ServiceDetailPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'services/:id/gallery',
        element: Load(ServiceGalleryPage),
      },
      {
        path: 'services/:id/faqs',
        element: Load(ServiceFAQsPage),
      },
      {
        path: 'services/:id/sub-services',
        element: Load(ServiceSubServicesPage),
      },
      {
        path: 'services/:id/vehicles',
        element: Load(ServiceVehiclesPage),
      },

      // ==================== CITIES MANAGEMENT ====================
      {
        path: 'cities',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['CITY_READ']}>
            {Load(CitiesPage)}
          </ProtectedRoute>
        ),
      },

      // ==================== SERVICE SETTINGS ====================
      {
        path: 'service-settings',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['SYSTEM_CONFIG_VIEW']}>
            {Load(ServiceSettingsPage)}
          </ProtectedRoute>
        ),
      },

      // ==================== PRICING RULES ====================
      {
        path: 'pricing/rules',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_BASE_PRICE_READ']}>
            {Load(PricingRulesPage)}
          </ProtectedRoute>
        ),
      },
      // Keeps legacy routes accessible just in case
      {
        path: 'pricing/base-prices',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_BASE_PRICE_READ']}>
            {Load(PricingRulesPage)}
          </ProtectedRoute>
        ),
      },
      // Legacy route for peak hours (redirect or reuse logic)
      {
        path: 'pricing/peak-hours',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_PEAK_PRICE_READ']}>
            {Load(PricingRulesPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'pricing/price-types',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_PRICE_TYPE_READ']}>
            {Load(VehiclePriceTypesManagement)}
          </ProtectedRoute>
        ),
      },

      // ==================== MAIN CONFIGURATIONS (SETTINGS) ====================
      {
        path: 'settings',
        element: <Navigate to="/settings/regions" replace />,
      },
      {
        path: 'settings/regions',
        element: Load(CitiesList),
      },
      {
        path: 'settings/branches',
        element: Load(BranchesList),
      },
      {
        path: 'settings/services',
        element: <Navigate to="/services" replace />,
      },
      {
        path: 'settings/goods-types',
        element: Load(GoodsTypesList),
      },
      {
        path: 'settings/recharge-plans',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['SUBSCRIPTION_PLAN_VIEW', 'TOPUP_PLAN_VIEW']}>
            {Load(RechargePlansPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/banners',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['BANNER_READ']}>
            {Load(BannersList)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/coupons',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['COUPON_READ']}>
            {Load(CouponsPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/customer-cancel-reasons',
        element: Load(CancelReasonsList),
      },
      {
        path: 'settings/agent-cancel-reasons',
        element: Load(CancelReasonsList),
      },
      {
        path: 'settings/app-controls',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['CONTROL_SETTING_READ']}>
            {Load(ControlSettingsPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/faqs',
        element: Load(FAQsList),
      },
      // Legacy paths for backward compatibility
      {
        path: 'settings/service-galleries',
        element: Load(ServiceGalleriesManagement),
      },
      {
        path: 'location/cities',
        element: <Navigate to="/cities" replace />,
      },
      {
        path: 'location/branches',
        element: <Navigate to="/settings/regions" replace />,
      },
      {
        path: 'branches',
        element: <Navigate to="/settings/regions" replace />,
      },
      {
        path: 'goods-types',
        element: <Navigate to="/content/goods-types" replace />,
      },
      {
        path: 'banners',
        element: <Navigate to="/content/banners" replace />,
      },
      {
        path: 'cancel-reasons',
        element: <Navigate to="/settings/customer-cancel-reasons" replace />,
      },
      {
        path: 'service-categories',
        element: <Navigate to="/services" replace />,
      },
      {
        path: 'service-galleries',
        element: Load(ServiceGalleriesManagement),
      },
      // ==================== CUSTOMER ====================
      {
        path: 'customer-details',
        element: <Navigate to="/customer-details/customers" replace />,
      },
      {
        path: 'customer-details/customers',
        element: Load(CustomersPage),
      },
      {
        path: 'customers',
        element: <Navigate to="/customer-details/customers" replace />,
      },

      // ==================== GOODS ====================
      {
        path: 'goods-details',
        element: <Navigate to="/goods-details/dashboard" replace />,
      },
      {
        path: 'goods-details/dashboard',
        element: Load(GoodsDashboard),
      },
      {
        path: 'goods-details/drivers',
        element: Load(GoodsDriversList),
      },
      {
        path: 'goods-details/drivers/:driverId/profile',
        element: Load(DriverProfile),
      },
      {
        path: 'goods-details/drivers/:driverId/wallet',
        element: Load(DriverWallet),
      },
      {
        path: 'goods-details/drivers/:driverId/rides',
        element: Load(DriverRides),
      },
      {
        path: 'goods-details/drivers/:driverId/ratings',
        element: Load(DriverRatings),
      },
      {
        path: 'goods-details/bookings',
        element: <Navigate to="/bookings?category=goods" replace />,
      },
      {
        path: 'goods-details/orders',
        element: <Navigate to="/bookings?category=goods" replace />,
      },
      // Legacy paths
      {
        path: 'goods/drivers',
        element: <Navigate to="/goods-details/drivers" replace />,
      },
      {
        path: 'goods/bookings',
        element: <Navigate to="/goods-details/bookings" replace />,
      },

      // ==================== CABS ====================
      {
        path: 'cabs-details',
        element: <Navigate to="/cabs-details/drivers" replace />,
      },
      {
        path: 'cabs-details/drivers',
        element: Load(DriversPage),
      },
      {
        path: 'cabs-details/bookings',
        element: <Navigate to="/bookings?category=cabs" replace />,
      },
      {
        path: 'cabs-details/orders',
        element: <Navigate to="/bookings?category=cabs" replace />,
      },
      // Legacy paths
      {
        path: 'cabs/drivers',
        element: <Navigate to="/cabs-details/drivers" replace />,
      },
      {
        path: 'cabs/bookings',
        element: <Navigate to="/cabs-details/bookings" replace />,
      },

      // ==================== DRIVERS (Other Services) ====================
      {
        path: 'drivers-details',
        element: <Navigate to="/drivers-details/agents" replace />,
      },
      {
        path: 'drivers-details/agents',
        element: Load(DriversPage),
      },
      {
        path: 'drivers-details/bookings',
        element: <Navigate to="/bookings?category=drivers" replace />,
      },
      {
        path: 'drivers-details/orders',
        element: <Navigate to="/bookings?category=drivers" replace />,
      },
      {
        path: 'drivers',
        element: <Navigate to="/drivers-details/agents" replace />,
      },

      // ==================== JCB/CRANE ====================
      {
        path: 'jcb-details',
        element: <Navigate to="/jcb-details/agents" replace />,
      },
      {
        path: 'jcb-details/agents',
        element: Load(DriversPage),
      },
      {
        path: 'jcb-details/bookings',
        element: <Navigate to="/bookings?category=jcb" replace />,
      },
      {
        path: 'jcb-details/orders',
        element: <Navigate to="/bookings?category=jcb" replace />,
      },
      // Legacy paths
      {
        path: 'jcb/bookings',
        element: <Navigate to="/jcb-details/bookings" replace />,
      },

      // ==================== HANDYMAN ====================
      {
        path: 'handyman-details',
        element: <Navigate to="/handyman-details/agents" replace />,
      },
      {
        path: 'handyman-details/agents',
        element: Load(DriversPage),
      },
      {
        path: 'handyman-details/bookings',
        element: <Navigate to="/bookings?category=handyman" replace />,
      },
      {
        path: 'handyman-details/orders',
        element: <Navigate to="/bookings?category=handyman" replace />,
      },
      // Legacy paths
      {
        path: 'handyman/bookings',
        element: <Navigate to="/handyman-details/bookings" replace />,
      },

      // ==================== PROVIDERS ====================
      {
        path: 'providers',
        element: Load(ProvidersPage),
      },
      {
        path: 'drivers-report',
        element: <Navigate to="/providers?tab=overview" replace />,
      },

      // ==================== DRIVER KYC ====================
      {
        path: 'drivers-kyc',
        element: <Navigate to="/providers?tab=kyc" replace />,
      },
      {
        path: 'drivers-kyc/:providerUuid',
        element: Load(DriverKycDetailPage),
      },

      // ==================== REPORTS ====================
      {
        path: 'reports',
        element: Load(ReportsPage),
      },

      // ==================== FLEET MANAGEMENT (Legacy/Additional) ====================
      {
        path: 'fleet',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_READ']}>
            {Load(FleetPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicles',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_READ']}>
            {Load(VehiclesManagement)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicles/goods',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_READ']}>
            <Suspense fallback={<PageSkeleton />}>
              <VehiclesManagement categoryName="Goods" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicles/cabs',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_READ']}>
            <Suspense fallback={<PageSkeleton />}>
              <VehiclesManagement categoryName="Cab" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicles/jcb',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_READ']}>
            <Suspense fallback={<PageSkeleton />}>
              <VehiclesManagement categoryName="JCB" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicle-types',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_TYPE_READ']}>
            {Load(VehicleTypesManagement)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/base-prices',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_BASE_PRICE_READ']}>
            {Load(VehicleBasePricesManagement)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicle-price/:vehicleTypeId/:vehicleTypeName',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_BASE_PRICE_READ']}>
            {Load(VehiclePricePage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicle-peak-hours-price/:vehicleTypeId/:vehicleTypeName',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_PEAK_PRICE_READ']}>
            {Load(VehiclePeakHoursPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicle-upgrade-prices/:vehicleTypeId/:vehicleTypeName',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_BASE_PRICE_READ']}>
            {Load(VehicleUpgradePricesPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/vehicle-guidelines/:vehicleTypeId/:vehicleTypeName',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['GUIDELINE_READ']}>
            {Load(VehicleGuidelinesPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'fleet/price-types',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['VEHICLE_PRICE_TYPE_READ']}>
            {Load(VehiclePriceTypesManagement)}
          </ProtectedRoute>
        ),
      },

      // ==================== BOOKINGS ====================
      {
        path: 'bookings',
        element: Load(BookingsPage),
      },
      {
        path: 'bookings/:bookingId/track',
        element: Load(LiveOrderTrackingPage),
      },

      // ==================== ADMIN & ROLES ====================
      {
        path: 'roles',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['ROLE_VIEW']}>
            {Load(RolesPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin-access',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['ADMIN_ASSIGN_BRANCH']}>
            {Load(AdminAccessManagement)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'admins',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['ADMIN_VIEW']}>
            {Load(AdminUsersPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
            {Load(AdminPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'api-test',
        element: Load(ApiTestPage),
      },

      // ==================== REFERRAL ====================
      {
        path: 'referral',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['REFERRAL_ANALYTICS_VIEW']}>
            {Load(ReferralAdminPage)}
          </ProtectedRoute>
        ),
      },

      // ==================== AUDIT LOGS ====================
      {
        path: 'audit-logs',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} allowedPermissions={['AUDIT_LOG_READ_SELF']}>
            {Load(AuditLogsPage)}
          </ProtectedRoute>
        ),
      },
    ],
  },
];
